import { useState, useEffect, useCallback } from "react";
import GameLayout from "../components/GameLayout";
import API from "../lib/api";
import { toast } from "sonner";
import { Wallet, ArrowRightLeft, ArrowUpRight, ArrowDownLeft, Clock, CheckCircle, XCircle, Zap } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const STATUS_ICON = { pending: Clock, approved: CheckCircle, rejected: XCircle };
const STATUS_COLOR = { pending: "#F59E0B", approved: "#10B981", rejected: "#EF4444" };

export default function WalletPage() {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [settings, setSettings] = useState({});
  const [cryptos, setCryptos] = useState([]);
  const [fpEnabled, setFpEnabled] = useState(false);
  const [fpDepositsOn, setFpDepositsOn] = useState(false);
  const [fpWithdrawalsOn, setFpWithdrawalsOn] = useState(false);
  const [tab, setTab] = useState("overview");
  const [convertAmt, setConvertAmt] = useState("");
  const [wdAmount, setWdAmount] = useState("");
  const [wdAddress, setWdAddress] = useState("");
  const [wdCrypto, setWdCrypto] = useState("BTC");
  const [dpMethod, setDpMethod] = useState("manual");
  const [wdMethod, setWdMethod] = useState("standard");
  const [dpAmount, setDpAmount] = useState("");
  const [dpProof, setDpProof] = useState("");
  const [dpCrypto, setDpCrypto] = useState("BTC");
  const [fpDepAmt, setFpDepAmt] = useState("");
  const [loading, setLoading] = useState("");
  const [cryptoRates, setCryptoRates] = useState({});
  const [depositWallets, setDepositWallets] = useState({});
  const [withdrawalFee, setWithdrawalFee] = useState(0.05);
  const [fpMinWd, setFpMinWd] = useState(0.10);
  const [fpWdFee, setFpWdFee] = useState(0.02);
  const [fpWdText, setFpWdText] = useState("");
  const [minWithdrawal, setMinWithdrawal] = useState(1);
  const [depositNetworks, setDepositNetworks] = useState({});
  const [depositWarning, setDepositWarning] = useState("");
  const [withdrawalWarning, setWithdrawalWarning] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/wallet");
      setWallet(data.wallet);
      setTransactions(data.transactions);
      setWithdrawals(data.withdrawals);
      setDeposits(data.deposits);
      setSettings(data.settings);
      setCryptos(data.supported_cryptos || ["BTC"]);
      setFpEnabled(data.faucetpay_enabled || false);
      setFpDepositsOn(data.faucetpay_deposits_enabled || false);
      setFpWithdrawalsOn(data.faucetpay_withdrawals_enabled || false);
      setCryptoRates(data.crypto_rates || {});
      setDepositWallets(data.deposit_wallets || {});
      setWithdrawalFee(data.withdrawal_fee || 0.05);
      setMinWithdrawal(data.min_withdrawal || 1);
      setFpMinWd(data.faucetpay_min_withdrawal || 0.10);
      setFpWdFee(data.faucetpay_withdrawal_fee || 0.02);
      setFpWdText(data.faucetpay_withdrawal_text || "");
      setDepositNetworks(data.deposit_networks || {});
      setDepositWarning(data.deposit_warning_text || "");
      setWithdrawalWarning(data.withdrawal_warning_text || "");
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-revert FaucetPay tabs to safe defaults when admin disables them
  useEffect(() => { if (!fpDepositsOn && dpMethod === "faucetpay") setDpMethod("manual"); }, [fpDepositsOn, dpMethod]);
  useEffect(() => { if (!fpWithdrawalsOn && wdMethod === "faucetpay") setWdMethod("standard"); }, [fpWithdrawalsOn, wdMethod]);

  const convert = async (e) => {
    e.preventDefault();
    setLoading("convert");
    try {
      const { data } = await API.post("/wallet/convert", { amount: parseFloat(convertAmt) });
      toast.success(data.message);
      setConvertAmt("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const withdraw = async (e) => {
    e.preventDefault();
    setLoading("withdraw");
    try {
      const { data } = await API.post("/wallet/withdraw", { amount: parseFloat(wdAmount), wallet_address: wdAddress, currency: wdCrypto });
      toast.success(data.message);
      setWdAmount(""); setWdAddress("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const depositManual = async (e) => {
    e.preventDefault();
    setLoading("deposit");
    try {
      const { data } = await API.post("/wallet/deposit", { amount: parseFloat(dpAmount), proof: dpProof, currency: dpCrypto });
      toast.success(data.message);
      setDpAmount(""); setDpProof("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const depositFaucetPay = async (e) => {
    e.preventDefault();
    setLoading("fp-deposit");
    try {
      const { data } = await API.post("/wallet/deposit-faucetpay", { amount: parseInt(fpDepAmt), currency: dpCrypto });
      toast.success(data.message);
      setFpDepAmt("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const rate = settings?.conversion_rate || 100;
  const fee = ((settings?.selling_fee || settings?.selling_tax || 0.05) * 100).toFixed(0);
  const tabs = ["overview", "convert", "withdraw", "deposit", "history"];

  return (
    <GameLayout title="Wallet">
      <div className="space-y-6 max-w-4xl" data-testid="wallet-page">
        {/* Balances — 3 separate */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-[#131722] rounded-xl border border-[#FFD600]/20 p-5">
            <p className="text-xs text-[#64748B] tracking-wide uppercase mb-2">Game Coins</p>
            <p className="font-mono-game text-2xl font-bold text-[#FFD600] glow-gold" data-testid="wallet-coins">{wallet?.coins?.toFixed(0) || 0}</p>
            <p className="text-[10px] text-[#64748B] mt-2">For upgrades, workers, game actions</p>
          </div>
          <div className="bg-[#131722] rounded-xl border border-[#10B981]/20 p-5">
            <p className="text-xs text-[#64748B] tracking-wide uppercase mb-2">Withdrawable</p>
            <p className="font-mono-game text-2xl font-bold text-[#10B981]" data-testid="wallet-balance">${wallet?.withdrawable_balance?.toFixed(2) || "0.00"}</p>
            <p className="text-[10px] text-[#64748B] mt-2">From coin conversion. Withdraw only</p>
          </div>
          <div className="bg-[#131722] rounded-xl border border-[#00F0FF]/20 p-5">
            <p className="text-xs text-[#64748B] tracking-wide uppercase mb-2">Deposit Balance</p>
            <p className="font-mono-game text-2xl font-bold text-[#00F0FF]" data-testid="wallet-deposit-balance">${wallet?.deposit_balance?.toFixed(2) || "0.00"}</p>
            <p className="text-[10px] text-[#64748B] mt-2">From deposits. Buy coin packs</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`wallet-tab-${t}`}
              className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-md transition-all whitespace-nowrap px-2 ${tab === t ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#64748B] hover:text-[#94A3B8]"}`}>
              {t}
            </button>
          ))}
        </div>

        {/* Convert */}
        {tab === "convert" && (
          <form onSubmit={convert} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4" data-testid="convert-form">
            <h3 className="text-lg font-bold text-[#F8FAFC]">Convert Coins to Withdrawable</h3>
            <p className="text-sm text-[#94A3B8]">Rate: {rate} coins = 1 withdrawable unit. Enter the coins amount you want to convert.</p>
            <div>
              <Label className="text-[#94A3B8] text-sm">Amount (coins)</Label>
              <Input type="number" step="1" value={convertAmt} onChange={(e) => setConvertAmt(e.target.value)} placeholder={`Min ${rate} coins`} required
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="convert-amount" />
              {convertAmt && parseFloat(convertAmt) >= rate && (
                <p className="text-xs text-[#10B981] mt-1">You'll receive: {(Math.floor(parseFloat(convertAmt) / rate) * rate / rate).toFixed(2)} units</p>
              )}
            </div>
            <button type="submit" disabled={loading === "convert"} data-testid="convert-submit-btn"
              className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50">
              <ArrowRightLeft className="w-4 h-4" /> {loading === "convert" ? "Converting..." : "Convert"}
            </button>
          </form>
        )}

        {/* Withdraw */}
        {tab === "withdraw" && (
          <div className="space-y-4">
            <div className="flex gap-1 bg-[#0B0E14] rounded-lg p-1 border border-[#2A3241]/30">
              <button onClick={() => setWdMethod("standard")} data-testid="withdraw-method-manual"
                className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-md ${wdMethod === "standard" ? "bg-[#10B981]/10 text-[#10B981]" : "text-[#64748B]"}`}>
                Standard Withdraw
              </button>
              {fpWithdrawalsOn && (
                <button onClick={() => setWdMethod("faucetpay")} data-testid="withdraw-method-faucetpay"
                  className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-md flex items-center justify-center gap-1 ${wdMethod === "faucetpay" ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B]"}`}>
                  <Zap className="w-3 h-3" /> FaucetPay
                </button>
              )}
            </div>

            {wdMethod !== "faucetpay" ? (
              <form onSubmit={withdraw} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4" data-testid="withdraw-form">
                <h3 className="text-lg font-bold text-[#F8FAFC]">Request Withdrawal</h3>
                <p className="text-sm text-[#94A3B8]">Submit a withdrawal request. Admin will review and process.</p>
                <div className="bg-[#0B0E14] rounded-lg p-3 border border-[#2A3241]/30 space-y-1" data-testid="withdraw-info-notes">
                  <p className="text-[10px] text-[#64748B]">Standard: Min <span className="text-[#F8FAFC] font-bold">${minWithdrawal}</span> &middot; Fee <span className="text-[#F8FAFC] font-bold">{(withdrawalFee*100).toFixed(0)}%</span></p>
                  {fpWithdrawalsOn && <p className="text-[10px] text-[#FFD600]">FaucetPay: Min <span className="font-bold">${fpMinWd}</span> &middot; Fee <span className="font-bold">{(fpWdFee*100).toFixed(0)}%</span></p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#94A3B8] text-sm">Amount</Label>
                    <Input type="number" step="0.01" value={wdAmount} onChange={(e) => setWdAmount(e.target.value)} required
                      className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="withdraw-amount" />
                    {wdAmount && parseFloat(wdAmount) > 0 && (
                      <div className="mt-2 bg-[#0B0E14] rounded-lg p-2 border border-[#2A3241]/30 space-y-1">
                        <div className="flex justify-between text-[10px]"><span className="text-[#64748B]">Requested:</span><span className="font-mono-game text-[#F8FAFC]">${parseFloat(wdAmount).toFixed(2)}</span></div>
                        <div className="flex justify-between text-[10px]"><span className="text-[#EF4444]">Fee ({(withdrawalFee*100).toFixed(0)}%):</span><span className="font-mono-game text-[#EF4444]">-${(parseFloat(wdAmount)*withdrawalFee).toFixed(4)}</span></div>
                        <div className="flex justify-between text-[10px] border-t border-[#2A3241]/30 pt-1"><span className="text-[#10B981] font-bold">You receive:</span><span className="font-mono-game font-bold text-[#10B981]">${(parseFloat(wdAmount)*(1-withdrawalFee)).toFixed(4)}</span></div>
                        {cryptoRates[wdCrypto] > 0 && <div className="flex justify-between text-[10px]"><span className="text-[#00F0FF]">≈ {wdCrypto}:</span><span className="font-mono-game text-[#00F0FF]">{((parseFloat(wdAmount)*(1-withdrawalFee))/cryptoRates[wdCrypto]).toFixed(8)}</span></div>}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-sm">Cryptocurrency</Label>
                    <select value={wdCrypto} onChange={(e) => setWdCrypto(e.target.value)} data-testid="withdraw-crypto"
                      className="w-full h-10 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
                      {cryptos.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <p className="text-[10px] text-[#64748B] mt-1">Network: {depositNetworks[wdCrypto] || `${wdCrypto} mainnet`}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-sm">Wallet Address ({wdCrypto})</Label>
                  <Input value={wdAddress} onChange={(e) => setWdAddress(e.target.value)} placeholder={`Your ${wdCrypto} address`} required
                    className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="withdraw-address" />
                </div>
                {withdrawalWarning && <p className="text-[10px] text-[#F59E0B]" data-testid="withdraw-warning">{withdrawalWarning}</p>}
                <button type="submit" disabled={loading === "withdraw"} data-testid="withdraw-submit-btn"
                  className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#10B981] text-white rounded-lg hover:bg-[#10B981]/90 disabled:opacity-50">
                  <ArrowUpRight className="w-4 h-4" /> {loading === "withdraw" ? "Submitting..." : "Request Withdrawal"}
                </button>
              </form>
            ) : (
              <form onSubmit={async (e) => { e.preventDefault(); setLoading("fp-wd"); try { const { data } = await API.post("/wallet/withdraw-faucetpay", { amount: parseFloat(wdAmount), currency: wdCrypto }); toast.success(data.message); setWdAmount(""); load(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } finally { setLoading(""); } }}
                className="bg-[#131722] rounded-xl border border-[#FFD600]/20 p-6 space-y-4" data-testid="withdraw-faucetpay-form">
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="w-5 h-5 text-[#FFD600]" />
                  <h3 className="text-lg font-bold text-[#F8FAFC]">FaucetPay Withdrawal</h3>
                </div>
                <p className="text-sm text-[#94A3B8]">{fpWdText || "Withdraw directly to your FaucetPay wallet."}</p>
                <div className="bg-[#0B0E14] rounded-lg p-3 border border-[#FFD600]/20 space-y-1" data-testid="fp-withdraw-info-notes">
                  <p className="text-[10px] text-[#FFD600]">FaucetPay: Min <span className="font-bold">${fpMinWd}</span> &middot; Fee <span className="font-bold">{(fpWdFee*100).toFixed(0)}%</span></p>
                  <p className="text-[10px] text-[#64748B]">Standard: Min <span className="text-[#F8FAFC]">${minWithdrawal}</span> &middot; Fee <span className="text-[#F8FAFC]">{(withdrawalFee*100).toFixed(0)}%</span></p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#94A3B8] text-sm">Amount</Label>
                    <Input type="number" step="0.01" value={wdAmount} onChange={(e) => setWdAmount(e.target.value)} required
                      className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="fp-withdraw-amount" />
                    {wdAmount && parseFloat(wdAmount) > 0 && (
                      <div className="mt-2 bg-[#0B0E14] rounded-lg p-2 border border-[#FFD600]/20 space-y-1">
                        <div className="flex justify-between text-[10px]"><span className="text-[#64748B]">Requested:</span><span className="font-mono-game text-[#F8FAFC]">${parseFloat(wdAmount).toFixed(2)}</span></div>
                        <div className="flex justify-between text-[10px]"><span className="text-[#EF4444]">Fee ({(fpWdFee*100).toFixed(0)}%):</span><span className="font-mono-game text-[#EF4444]">-${(parseFloat(wdAmount)*fpWdFee).toFixed(4)}</span></div>
                        <div className="flex justify-between text-[10px] border-t border-[#2A3241]/30 pt-1"><span className="text-[#10B981] font-bold">You receive:</span><span className="font-mono-game font-bold text-[#10B981]">${(parseFloat(wdAmount)*(1-fpWdFee)).toFixed(4)}</span></div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-sm">Cryptocurrency</Label>
                    <select value={wdCrypto} onChange={(e) => setWdCrypto(e.target.value)} data-testid="fp-withdraw-crypto"
                      className="w-full h-10 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
                      {cryptos.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <button type="submit" disabled={loading === "fp-wd"} data-testid="fp-withdraw-submit-btn"
                  className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 disabled:opacity-50 shadow-[0_0_15px_rgba(255,214,0,0.3)]">
                  <Zap className="w-4 h-4" /> {loading === "fp-wd" ? "Processing..." : "Withdraw via FaucetPay"}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Deposit */}
        {tab === "deposit" && (
          <div className="space-y-4">
            {/* Deposit method selector */}
            <div className="flex gap-1 bg-[#0B0E14] rounded-lg p-1 border border-[#2A3241]/30">
              <button onClick={() => setDpMethod("manual")} data-testid="deposit-method-manual"
                className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-md ${dpMethod === "manual" ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B]"}`}>
                External Wallet
              </button>
              {fpDepositsOn && (
                <button onClick={() => setDpMethod("faucetpay")} data-testid="deposit-method-faucetpay"
                  className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-md flex items-center justify-center gap-1 ${dpMethod === "faucetpay" ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B]"}`}>
                  <Zap className="w-3 h-3" /> FaucetPay
                </button>
              )}
            </div>

            {dpMethod === "manual" ? (
              <form onSubmit={depositManual} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4" data-testid="deposit-manual-form">
                <h3 className="text-lg font-bold text-[#F8FAFC]">Deposit from External Wallet</h3>
                <p className="text-sm text-[#94A3B8]">Send crypto to the address below, then submit the transaction hash as proof.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#94A3B8] text-sm">Amount (USD)</Label>
                    <Input type="number" step="0.01" value={dpAmount} onChange={(e) => setDpAmount(e.target.value)} required
                      className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="deposit-amount" />
                    {dpAmount && cryptoRates[dpCrypto] > 0 && (
                      <p className="text-xs text-[#00F0FF] mt-1 font-mono-game">
                        ≈ {(parseFloat(dpAmount) / cryptoRates[dpCrypto]).toFixed(8)} {dpCrypto}
                      </p>
                    )}
                    <p className="text-[10px] text-[#64748B] mt-0.5">Conversion is automatic</p>
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-sm">Cryptocurrency</Label>
                    <select value={dpCrypto} onChange={(e) => setDpCrypto(e.target.value)} data-testid="deposit-crypto"
                      className="w-full h-10 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
                      {cryptos.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                {/* Deposit wallet address */}
                {depositWallets[dpCrypto] && (
                  <div className="bg-[#0B0E14] rounded-lg p-4 border border-[#00F0FF]/20">
                    <p className="text-xs text-[#00F0FF] uppercase tracking-wider mb-1">Send {dpCrypto} to this address:</p>
                    <p className="font-mono-game text-sm text-[#F8FAFC] break-all select-all">{depositWallets[dpCrypto]}</p>
                    <p className="text-[10px] text-[#64748B] mt-1">Network: {depositNetworks[dpCrypto] || `${dpCrypto} mainnet`}</p>
                    {depositWarning && <p className="text-[10px] text-[#F59E0B] mt-1.5" data-testid="deposit-warning">{depositWarning}</p>}
                  </div>
                )}
                <div>
                  <Label className="text-[#94A3B8] text-sm">Transaction Hash / TXID</Label>
                  <Input value={dpProof} onChange={(e) => setDpProof(e.target.value)} placeholder="Paste the TXID from your wallet" required
                    className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 font-mono-game" data-testid="deposit-proof" />
                  <p className="text-[10px] text-[#64748B] mt-0.5">Must be unique — duplicate TXIDs are rejected</p>
                </div>
                <button type="submit" disabled={loading === "deposit"} data-testid="deposit-submit-btn"
                  className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 disabled:opacity-50">
                  <ArrowDownLeft className="w-4 h-4" /> {loading === "deposit" ? "Submitting..." : "Submit Deposit"}
                </button>
              </form>
            ) : (
              <form onSubmit={depositFaucetPay} className="bg-[#131722] rounded-xl border border-[#FFD600]/20 p-6 space-y-4" data-testid="deposit-faucetpay-form">
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="w-5 h-5 text-[#FFD600]" />
                  <h3 className="text-lg font-bold text-[#F8FAFC]">Instant FaucetPay Deposit</h3>
                </div>
                <p className="text-sm text-[#94A3B8]">Deposit directly from your FaucetPay balance. Coins are credited instantly.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#94A3B8] text-sm">Amount (satoshi)</Label>
                    <Input type="number" value={fpDepAmt} onChange={(e) => setFpDepAmt(e.target.value)} placeholder="Amount in satoshi" required
                      className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="fp-deposit-amount" />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-sm">Cryptocurrency</Label>
                    <select value={dpCrypto} onChange={(e) => setDpCrypto(e.target.value)} data-testid="fp-deposit-crypto"
                      className="w-full h-10 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
                      {cryptos.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <button type="submit" disabled={loading === "fp-deposit"} data-testid="fp-deposit-submit-btn"
                  className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 disabled:opacity-50 shadow-[0_0_15px_rgba(255,214,0,0.3)]">
                  <Zap className="w-4 h-4" /> {loading === "fp-deposit" ? "Processing..." : "Deposit via FaucetPay"}
                </button>
              </form>
            )}
          </div>
        )}

        {tab === "overview" && (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6">
            <h3 className="text-lg font-bold text-[#F8FAFC] mb-4">Wallet Info</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-[#0B0E14] rounded-lg p-4 border border-[#2A3241]/30">
                <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Connected Address</p>
                <p className="text-sm text-[#F8FAFC] font-mono-game break-all">{wallet?.wallet_address || wallet?.faucetpay_address || "Not connected"}</p>
              </div>
              <div className="bg-[#0B0E14] rounded-lg p-4 border border-[#2A3241]/30">
                <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">FaucetPay</p>
                <p className="text-sm" style={{ color: fpEnabled ? "#10B981" : "#64748B" }}>{fpEnabled ? "Available for deposits & withdrawals" : "Not configured by admin"}</p>
              </div>
              <div className="bg-[#0B0E14] rounded-lg p-4 border border-[#2A3241]/30">
                <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Selling Fee</p>
                <p className="text-sm text-[#FFD600] font-mono-game">{fee}%</p>
              </div>
              <div className="bg-[#0B0E14] rounded-lg p-4 border border-[#2A3241]/30">
                <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">Supported Cryptos</p>
                <div className="flex flex-wrap gap-1 mt-1">{cryptos.map(c => <span key={c} className="text-[10px] font-mono-game px-2 py-0.5 rounded bg-[#131722] text-[#00F0FF] border border-[#00F0FF]/20">{c}</span>)}</div>
              </div>
            </div>
          </div>
        )}

        {/* History */}
        {tab === "history" && (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6">
            <h3 className="text-lg font-bold text-[#F8FAFC] mb-4">Transaction History</h3>
            {transactions.length === 0 ? (
              <p className="text-sm text-[#94A3B8]">No transactions yet</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {transactions.slice(0, tab === "history" ? 50 : 10).map((tx, i) => (
                  <div key={tx.created_at || i} className="flex items-center justify-between py-3 border-b border-[#2A3241]/30 last:border-0">
                    <div className="flex items-center gap-3">
                      {tx.type === "sell" ? <ArrowUpRight className="w-4 h-4 text-[#10B981]" /> : <ArrowDownLeft className="w-4 h-4 text-[#3B82F6]" />}
                      <div>
                        <p className="text-sm text-[#F8FAFC] capitalize">{tx.type}{tx.resource ? ` - ${tx.resource}` : ""}</p>
                        <p className="text-xs text-[#64748B]">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ""}</p>
                      </div>
                    </div>
                    <span className="font-mono-game text-sm text-[#FFD600]">+{tx.net?.toFixed(1) || tx.units_received || tx.amount || 0}</span>
                  </div>
                ))}
              </div>
            )}

            {(withdrawals.length > 0 || deposits.length > 0) && (
              <div className="mt-6 pt-4 border-t border-[#2A3241]/30">
                <h4 className="text-sm font-bold text-[#F8FAFC] mb-3">Requests</h4>
                {[...withdrawals.map(w => ({...w, _type: "withdrawal"})), ...deposits.map(d => ({...d, _type: "deposit"}))].sort((a,b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 15).map((req, i) => {
                  const SIcon = STATUS_ICON[req.status] || Clock;
                  const sColor = STATUS_COLOR[req.status] || "#94A3B8";
                  return (
                    <div key={req._type + (req.withdrawal_id || req.deposit_id || i)} className="flex items-center justify-between py-2 border-b border-[#2A3241]/20 last:border-0">
                      <div className="flex items-center gap-2">
                        <SIcon className="w-4 h-4" style={{ color: sColor }} />
                        <span className="text-sm text-[#F8FAFC] capitalize">{req._type}</span>
                        {req.currency && <span className="text-[10px] font-mono-game text-[#64748B] bg-[#0B0E14] px-1.5 py-0.5 rounded">{req.currency}</span>}
                      </div>
                      <div className="text-right">
                        <span className="font-mono-game text-sm text-[#F8FAFC]">${req.amount}</span>
                        <p className="text-xs capitalize" style={{ color: sColor }}>{req.status}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </GameLayout>
  );
}
